import { useCallback, useEffect, useMemo, useState } from "react";

import { format, startOfDay } from "date-fns";
import { ArrowRight, CalendarDays, ChevronDown, ChevronRight, Flag, ListTodo, MoreVertical, SortAsc } from "lucide-react";

import { useApi } from "../../context/ApiContext";
import { useData } from "../../context/DataContext";
import TaskPreview from "../../features/Tasks/component/TaskPreview";
import TaskViewDialog from "../../features/Tasks/component/TaskViewDialog";
import { Badge } from "../ui/badge";
import { Card } from "../ui/card";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

import GanttChart from "./GanttChart";

const MilestoneGantt = ({ viewMode = "WEEKLY", projectId = null, title: customTitle, isDashboard = false, onViewModeChange }) => {
  const api = useApi();
  const { globalProjectId, projects } = useData();

  const activeProjectId = projectId !== null ? projectId : globalProjectId;

  const [milestones, setMilestones] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [milestoneSortConfig, setMilestoneSortConfig] = useState({});
  const [expandedMilestones, setExpandedMilestones] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [internalViewingTask, setInternalViewingTask] = useState(null);

  const fetchMilestones = useCallback(async () => {
    if (!activeProjectId) {
      setMilestones([]);
      return;
    }
    try {
      setIsLoading(true);
      const params = { size: 500 };
      if (activeProjectId !== "ALL") {
        params.projectId = activeProjectId;
      }
      const response = await api.get("/milestones", { params });
      setMilestones(response.data.items || []);
    } catch (err) {
      console.error("Failed to fetch milestones for Gantt:", err);
    } finally {
      setIsLoading(false);
    }
  }, [api, activeProjectId]);

  const fetchAllTasks = useCallback(async () => {
    if (!activeProjectId) {
      setAllTasks([]);
      return;
    }
    try {
      const params = { size: 1000 };
      if (activeProjectId !== "ALL") {
        params.projectId = activeProjectId;
      }
      const res = await api.get("/tasks", { params });
      setAllTasks(res.data.items || []);
    } catch (err) {
      console.error("Failed to fetch all tasks for Gantt:", err);
    }
  }, [api, activeProjectId]);

  const toggleMilestone = (milestoneId) => {
    setExpandedMilestones((prev) => {
      const next = new Set(prev);
      const idStr = String(milestoneId);
      if (next.has(idStr)) next.delete(idStr);
      else next.add(idStr);
      return next;
    });
  };

  const handleSortChange = (milestoneId, sortBy) => {
    setMilestoneSortConfig((prev) => ({ ...prev, [milestoneId]: sortBy }));
  };

  useEffect(() => {
    const refresh = async () => {
      if (activeProjectId) {
        await Promise.all([fetchMilestones(), fetchAllTasks()]);
      } else {
        setMilestones([]);
        setAllTasks([]);
      }
    };
    refresh();
  }, [fetchMilestones, fetchAllTasks, viewMode, activeProjectId]);

  useEffect(() => {
    const handleTaskUpdated = () => {
      if (activeProjectId) {
        fetchMilestones();
        fetchAllTasks();
      }
    };
    window.addEventListener("taskUpdated", handleTaskUpdated);
    return () => window.removeEventListener("taskUpdated", handleTaskUpdated);
  }, [fetchMilestones, fetchAllTasks, activeProjectId]);

  const flattenedData = useMemo(() => {
    const isTodayMode = viewMode?.toUpperCase() === "TODAY";
    const today = startOfDay(new Date());
    const rows = [];

    const isVisible = (item) => {
      if (!isTodayMode) return true;
      const startVal = item.startDate || item.createdAt || item.targetDate;
      const endVal = item.dueDate || item.targetDate || startVal;
      if (!startVal || !endVal) return false;
      const start = startOfDay(new Date(startVal));
      const end = startOfDay(new Date(endVal));
      return start <= today && end >= today;
    };

    const tasksByMilestone = {};

    allTasks.forEach((t) => {
      if (!isVisible(t)) return;
      const mIdRaw = t.milestoneId || (t.milestone && t.milestone.id);
      const mId = mIdRaw ? String(mIdRaw) : "uncategorized";
      if (!tasksByMilestone[mId]) tasksByMilestone[mId] = [];
      tasksByMilestone[mId].push(t);
    });

    // Sort tasks within each milestone group client-side for reliability
    const sortTasks = (taskList, mId) => {
      const sortBy = milestoneSortConfig[mId] || "startDate";
      return [...taskList].sort((a, b) => {
        let valA, valB;
        if (sortBy === "startDate") {
          valA = new Date(a.startDate || a.createdAt || 0);
          valB = new Date(b.startDate || b.createdAt || 0);
        } else if (sortBy === "dueDate") {
          valA = new Date(a.dueDate || a.targetDate || 0);
          valB = new Date(b.dueDate || b.targetDate || 0);
        } else {
          // creation date
          valA = new Date(a.createdAt || 0);
          valB = new Date(b.createdAt || 0);
        }
        return valA - valB;
      });
    };

    const sortedMilestones = [...milestones].sort((a, b) => {
      const dateA = new Date(a.startDate || a.targetDate || 0);
      const dateB = new Date(b.startDate || b.targetDate || 0);
      return dateA - dateB;
    });

    const groupColors = [
      "border-amber-500",
      "border-emerald-500",
      "border-sky-500",
      "border-violet-500",
      "border-rose-500",
      "border-indigo-500",
      "border-teal-500",
      "border-cyan-500",
      "border-orange-500",
      "border-fuchsia-500"
    ];

    sortedMilestones.forEach((m, idx) => {
      const groupColor = groupColors[idx % groupColors.length];
      const milestoneTasks = sortTasks(tasksByMilestone[String(m.id)] || [], String(m.id));
      const showMilestone = isVisible(m) || milestoneTasks.length > 0;
      if (showMilestone) {
        const isMExpanded = expandedMilestones.has(String(m.id));
        const mRow = {
          type: "milestone",
          item: m,
          level: 0,
          isExpanded: isMExpanded,
          groupColor,
          isFirstInGroup: true
        };

        if (!isMExpanded || milestoneTasks.length === 0) {
          mRow.isLastInGroup = true;
          rows.push(mRow);
        } else {
          rows.push(mRow);
          milestoneTasks.forEach((t, tIdx) => {
            rows.push({
              type: "task",
              item: t,
              level: 1,
              groupColor,
              isLastInGroup: tIdx === milestoneTasks.length - 1
            });
          });
        }
      }
    });

    if (tasksByMilestone["uncategorized"]?.length > 0) {
      const groupColor = "border-slate-400";
      const uRow = {
        type: "milestone",
        item: { id: "uncategorized", name: "General Tasks" },
        level: 0,
        groupColor,
        isFirstInGroup: true
      };

      const sortedUncategorized = sortTasks(tasksByMilestone["uncategorized"], "uncategorized");

      if (!expandedMilestones.has("uncategorized")) {
        uRow.isLastInGroup = true;
        rows.push(uRow);
      } else {
        rows.push(uRow);
        sortedUncategorized.forEach((t, tIdx) => {
          rows.push({
            type: "task",
            item: t,
            level: 1,
            groupColor,
            isLastInGroup: tIdx === sortedUncategorized.length - 1
          });
        });
      }
    }
    return rows;
  }, [milestones, allTasks, expandedMilestones, viewMode, milestoneSortConfig]);

  const getStatus = (item, type) => {
    const now = new Date();
    if (type === "milestone") {
      if (item.status === "COMPLETED") return "COMPLETED";
      if (item.targetDate && new Date(item.targetDate) < now) return "OVERDUE";
      return item.status;
    }
    if (item.status === "COMPLETED") return "COMPLETED";
    if (item.dueDate && new Date(item.dueDate) < now) return "OVERDUE";
    return item.status;
  };

  const getStatusStyles = (status, type = "task") => {
    if (type === "milestone") {
      switch (status) {
        case "COMPLETED":
          return "bg-emerald-500/10 border-emerald-500/50 text-emerald-700 dark:text-emerald-400";
        case "OVERDUE":
          return "bg-rose-500/10 border-rose-500/50 text-rose-700 dark:text-rose-400";
        default:
          return "bg-yale-blue-500/10 border-yale-blue-500/50 text-yale-blue-700 dark:text-yale-blue-300";
      }
    }
    switch (status) {
      case "COMPLETED":
        return "bg-emerald-500/90 border-emerald-600 shadow-emerald-500/20";
      case "IN_PROGRESS":
        return "bg-primary/90 border-yale-blue-600 shadow-primary/20";
      case "OVERDUE":
        return "bg-rose-500/90 border-rose-600 shadow-rose-500/20";
      default:
        return "bg-slate-400/90 border-yale-blue-500 shadow-yale-blue-500/20";
    }
  };

  const getHasTasks = useCallback(
    (milestoneId) => {
      if (milestoneId === "uncategorized") {
        return allTasks.some((t) => !t.milestoneId && !t.milestone?.id);
      }
      return allTasks.some((t) => String(t.milestoneId || t.milestone?.id) === String(milestoneId));
    },
    [allTasks]
  );

  const renderLabel = ({ item, level, type }) => {
    const isMilestone = type === "milestone";
    const isExpanded = expandedMilestones.has(String(item.id));
    const status = getStatus(item, type);
    const hasTasks = isMilestone ? getHasTasks(item.id) : false;

    return (
      <div className="flex items-center justify-between min-w-0">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {isMilestone ? (
            hasTasks ? (
              <button
                onClick={() => toggleMilestone(item.id)}
                className="p-1 hover:bg-slate-200 dark:hover:bg-yale-blue-800 rounded transition-colors flex-shrink-0"
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-yale-blue-600 dark:text-yale-blue-400" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-yale-blue-600 dark:text-yale-blue-400" />
                )}
              </button>
            ) : (
              <div className="w-6 flex-shrink-0" />
            )
          ) : level > 0 ? (
            <div className="w-6 flex-shrink-0" />
          ) : null}

          {isMilestone && (
            <Flag className={`h-3.5 w-3.5 flex-shrink-0 ${status === "OVERDUE" ? "text-rose-500" : "text-yale-blue-500"}`} />
          )}

          <div className="flex flex-col min-w-0 flex-1">
            <div className="flex items-center gap-2">
              {!isMilestone && <ListTodo className="h-3 w-3 text-yale-blue-400 flex-shrink-0" />}
              <span
                className={`text-sm ${isMilestone ? "font-black uppercase tracking-tight" : "font-bold"} text-yale-blue-800 dark:text-slate-100 truncate leading-tight ${
                  isMilestone && hasTasks ? "group-hover:text-yale-blue-600 cursor-pointer" : ""
                } transition-colors`}
                onClick={() => isMilestone && hasTasks && toggleMilestone(item.id)}
              >
                {item.name || item.title}
              </span>
            </div>

            <div className="flex flex-col gap-1 mt-1">
              {isMilestone && (item.startDate || item.targetDate) && (
                <div className="hidden md:flex items-center gap-1.5 text-[9px] text-slate-500 font-bold uppercase tracking-wider">
                  <CalendarDays className="h-2.5 w-2.5" />
                  <span>{item.startDate ? format(new Date(item.startDate), "dd/MM/yyyy") : "N/A"}</span>
                  <ArrowRight className="h-2 w-2 opacity-50" />
                  <span>{item.targetDate ? format(new Date(item.targetDate), "dd/MM/yyyy") : "N/A"}</span>
                </div>
              )}

              {!isMilestone && (
                <div className="flex flex-row items-center gap-1.5 mt-0.5">
                  <Badge className="text-[7px] md:text-[8px] uppercase font-black px-1 md:px-1.5 py-0 bg-slate-100 dark:bg-yale-blue-800 text-yale-blue-500 border-none w-fit flex-shrink-0">
                    {item.priority}
                  </Badge>
                  <div className="hidden md:flex items-center gap-1 text-[10px] text-slate-400 font-medium">
                    <CalendarDays className="h-2.5 w-2.5" />
                    <span>{item.startDate ? format(new Date(item.startDate), "dd/MM/yyyy") : "N/A"}</span>
                    <ArrowRight className="h-2 w-2 opacity-50" />
                    <span>{item.dueDate ? format(new Date(item.dueDate), "dd/MM/yyyy") : "N/A"}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {isMilestone && hasTasks && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-1 hover:bg-slate-200 dark:hover:bg-yale-blue-800 rounded transition-colors ml-auto opacity-0 group-hover:opacity-100">
                <MoreVertical className="h-3.5 w-3.5 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <div className="px-2 py-1.5 text-xs font-bold text-slate-500 flex items-center gap-2">
                <SortAsc className="h-3 w-3" />
                Sort Tasks By
              </div>
              <DropdownMenuItem
                onClick={() => handleSortChange(item.id, "startDate")}
                className={
                  milestoneSortConfig[item.id] === "startDate" || !milestoneSortConfig[item.id]
                    ? "bg-slate-100 dark:bg-yale-blue-800"
                    : ""
                }
              >
                Start Date
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleSortChange(item.id, "dueDate")}
                className={milestoneSortConfig[item.id] === "dueDate" ? "bg-slate-100 dark:bg-yale-blue-800" : ""}
              >
                End Date
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleSortChange(item.id, "createdAt")}
                className={milestoneSortConfig[item.id] === "createdAt" ? "bg-slate-100 dark:bg-yale-blue-800" : ""}
              >
                Creation Date
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    );
  };

  const renderBar = (row, position) => {
    const { item, type } = row;
    const isMilestone = type === "milestone";
    const status = getStatus(item, type);
    const styles = getStatusStyles(status, type);
    const hasTasks = isMilestone ? getHasTasks(item.id) : false;

    if (isMilestone) {
      return (
        <div
          className={`absolute h-7 rounded-lg border-2 flex items-center px-3 z-10 transition-all duration-300 shadow-md hover:shadow-lg ${
            hasTasks ? "cursor-pointer hover:translate-y-[-2px] hover:brightness-110 active:scale-[0.98]" : ""
          } overflow-hidden ${styles}`}
          style={{ left: position.left, width: position.width }}
          onClick={() => hasTasks && toggleMilestone(item.id)}
        >
          <div
            className="absolute inset-0 bg-current opacity-[0.25] z-0 border-r border-current/30"
            style={{ width: `${item.completionPercentage || 0}%` }}
          />
          <div className="relative z-10 flex items-center justify-between w-full gap-2">
            <span className="text-[9px] font-black truncate uppercase tracking-widest drop-shadow-md hidden sm:inline-block">
              {item.name}
            </span>
            <span className="text-[9px] font-black truncate uppercase tracking-widest drop-shadow-md sm:hidden">M</span>
            {item.completionPercentage > 0 && (
              <span className="text-[8px] font-bold opacity-80 flex-shrink-0 bg-white/40 dark:bg-black/20 px-1 rounded">
                {item.completionPercentage}%
              </span>
            )}
          </div>
        </div>
      );
    }

    return (
      <Popover>
        <PopoverTrigger asChild>
          <div
            className={`absolute h-9 rounded-xl border-2 cursor-pointer flex items-center px-4 z-10 transition-all duration-300 hover:translate-y-[-2px] hover:brightness-110 shadow-lg hover:shadow-xl active:scale-95 group/bar ${styles}`}
            style={{ left: position.left, width: position.width }}
            onClick={() => setInternalViewingTask(item)}
          >
            <div className="flex items-center gap-2 w-full overflow-hidden">
              {item.status === "COMPLETED" && (
                <svg className="w-3.5 h-3.5 text-white flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
              <span className="text-[10px] text-white font-black truncate uppercase tracking-widest drop-shadow-md hidden sm:inline-block">
                {item.title}
              </span>
              <span className="text-[10px] text-white font-black truncate uppercase tracking-widest drop-shadow-md sm:hidden">
                T
              </span>
            </div>
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-[90vw] md:w-[500px] p-0 border-none shadow-2xl rounded-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
          <TaskPreview task={item} />
        </PopoverContent>
      </Popover>
    );
  };

  const dynamicTitle = useMemo(() => {
    if (customTitle) return customTitle;
    if (activeProjectId && activeProjectId !== "ALL") {
      const project = projects.find((p) => p.id === activeProjectId);
      return `Timeline: ${project?.name || "Project"}`;
    }
    return "Overall Project Timeline";
  }, [customTitle, activeProjectId, projects]);

  if (!activeProjectId) {
    return (
      <Card className="overflow-hidden border-slate-200 dark:border-yale-blue-800 shadow-xl rounded-2xl bg-white dark:bg-yale-blue-950 p-8 md:p-12 text-center flex flex-col items-center justify-center min-h-[400px]">
        <div className="p-4 rounded-full bg-slate-100 dark:bg-yale-blue-900/30 text-yale-blue-500 dark:text-yale-blue-300 mb-6 animate-pulse">
          <CalendarDays className="h-12 w-12" />
        </div>
        <h3 className="text-xl font-bold text-yale-blue-800 dark:text-slate-100 mb-2">Select a Project to View Timeline</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm mb-6 leading-relaxed">
          Please select a specific project from the header dropdown to view its milestone schedules, task breakdowns, and Gantt
          chart timeline.
        </p>
      </Card>
    );
  }

  return (
    <>
      <GanttChart
        data={flattenedData}
        viewMode={viewMode}
        title={dynamicTitle}
        subtitle={`Visualizing ${milestones.length} milestones and ${allTasks.length} tasks`}
        renderLabel={renderLabel}
        renderBar={renderBar}
        isDashboard={isDashboard}
        onViewModeChange={onViewModeChange}
      />
      <TaskViewDialog task={internalViewingTask} isOpen={!!internalViewingTask} onClose={() => setInternalViewingTask(null)} />
    </>
  );
};

export default MilestoneGantt;
